import { Alert } from '@mui/material';
import Spinner from 'components/shared/Spinner';
import React, { useEffect, useState } from 'react';
import UserService from 'services/api/UserService';
import SiteConfig, { ModulesSettingsConfig, SiteConfigView } from 'store/types/SiteConfig';
import useRequest from 'hooks/useRequest';
import SiteTheme from 'store/enums/SiteTheme';
import { tenantConfig } from 'config';
import { PaymentConfiguration, SolupayItem } from 'store/types/TenantConfig';

import commonStyles from 'styles/common.module.scss';
import PaymentProvider from 'store/enums/PaymentProvider';

const getDefaultModulesSettingsData = (data?: ModulesSettingsConfig): ModulesSettingsConfig => ({
  profile: {
    enableMyEmployer: data?.profile?.enableMyEmployer,
    enableRelationship: data?.profile?.enableRelationship,
    enablePreferences: data?.profile?.enablePreferences,
    enableAdvocacy: data?.profile?.enableAdvocacy,
    myEmployer: data?.profile?.myEmployer,
    relationship: data?.profile?.relationship,
    preferences: data?.profile?.preferences,
    advocacy: data?.profile?.advocacy,
    hiddenTabs: data?.profile?.hiddenTabs,
  },
  companyManagement: {
    advocacy: data?.companyManagement?.advocacy,
    enableAdvocacy: data?.companyManagement?.enableAdvocacy,
    disabledFields: data?.companyManagement?.disabledFields ?? {},
    hiddenFields: data?.companyManagement?.hiddenFields ?? {},
    hiddenTabs: data?.companyManagement?.hiddenTabs ?? {},
    enableDirectoryListings: data?.companyManagement?.enableDirectoryListings ?? false,
    enableContactBirthdate: data?.companyManagement?.enableContactBirthdate ?? false,
    enableContactFileUpload: data?.companyManagement?.enableContactFileUpload ?? false,
  },
  settings: {
    enableAccountSettings: data?.settings?.enableAccountSettings,
    enableEmailNotifications: data?.settings?.enableEmailNotifications,
    enablePortalManagement: data?.settings?.enablePortalManagement,
    hiddenTabs: data?.settings?.hiddenTabs,
  },
  personalPayments: {
    enablePayInvoices: data?.personalPayments?.enablePayInvoices,
    enablePaySalesOrders: data?.personalPayments?.enablePaySalesOrders,
    hiddenTabs: data?.personalPayments?.hiddenTabs,
  },
  companyPayments: {
    enablePaySalesOrders: data?.companyPayments?.enablePaySalesOrders,
    partnerPayLink: data?.companyPayments?.partnerPayLink,
    hiddenTabs: data?.companyPayments?.hiddenTabs,
  },
  groupPayments: {
    hiddenTabs: data?.groupPayments?.hiddenTabs,
  },
  addresses: {
    enableSelectDefaultAddress: data?.addresses?.enableSelectDefaultAddress,
  },
  home: {
    enableNavigationSection: data?.home?.enableNavigationSection,
    enableNotes: data?.home?.enableNotes,
    navigationLinks: data?.home?.navigationLinks,
  },
  subscriptions: {
    enableDonations: data?.subscriptions?.enableDonations,
    numOfDaysToRenew: data?.subscriptions?.numOfDaysToRenew || 0,
  },
  partner: {
    applicationTypeId: data?.partner?.applicationTypeId,
    hiddenTabs: data?.partner?.hiddenTabs,
  },
  payments: {
    enableSalesOrderHistory: data?.payments?.enableSalesOrderHistory,
    enableRecaptcha: data?.payments?.enableRecaptcha,
    events: {
      isAchEnabled: data?.payments?.events?.isAchEnabled,
      isCreditCardEnabled: data?.payments?.events?.isCreditCardEnabled,
    },
    invoices: {
      isAchEnabled: data?.payments?.invoices?.isAchEnabled,
      isCreditCardEnabled: data?.payments?.invoices?.isCreditCardEnabled,
    },
    donations: {
      isAchEnabled: data?.payments?.donations?.isAchEnabled,
      isCreditCardEnabled: data?.payments?.donations?.isCreditCardEnabled,
    },
    applications: {
      isAchEnabled: data?.payments?.applications?.isAchEnabled,
      isCreditCardEnabled: data?.payments?.applications?.isCreditCardEnabled,
    },
    products: {
      isAchEnabled: data?.payments?.products?.isAchEnabled,
      isCreditCardEnabled: data?.payments?.products?.isCreditCardEnabled,
    },
  },
  donations: {
    predefinedAmounts: data?.donations?.predefinedAmounts || [],
    defaultFeePercent: data?.donations?.defaultFeePercent || 0,
    isParentCampaignAllowed: data?.donations?.isParentCampaignAllowed,
    enableGuestCheckout: data?.donations?.enableGuestCheckout,
    enableSimpleViewOnEmbed: data?.donations?.enableSimpleViewOnEmbed ?? false,
  },
  nccer: {
    portalHeader: data?.nccer?.portalHeader || '',
    enableCardNumber: data?.nccer?.enableCardNumber,
  },
  events: {
    pageTitle: data?.events?.pageTitle || '',
    pageSubTitle: data?.events?.pageSubTitle || '',
  },
  memberDirectory: {
    pageTitle: data?.memberDirectory?.pageTitle || '',
    pageSubTitle: data?.memberDirectory?.pageSubTitle || '',
    enableMarketSegments: data?.memberDirectory?.enableMarketSegments,
  },
  publicCommittees: {
    pageTitle: data?.publicCommittees?.pageTitle || '',
    pageSubTitle: data?.publicCommittees?.pageSubTitle || '',
  },
  expenseProfile: {
    profileTitle: data?.expenseProfile?.profileTitle || '',
    profilePaymentOptionsTitle: data?.expenseProfile?.profilePaymentOptionsTitle || '',
    profileTaxFilesTitle: data?.expenseProfile?.profileTaxFilesTitle || '',
    hiddenTabs: data?.expenseProfile?.hiddenTabs,
  },
  expenseReport: {
    reportsTitle: data?.expenseReport?.reportsTitle || '',
    reportExpensesTitle: data?.expenseReport?.reportExpensesTitle || '',
    hiddenTabs: data?.expenseReport?.hiddenTabs,
  },
  applications: {
    applicationProgressCalculations: data?.applications?.applicationProgressCalculations || [],
    hiddenTabs: data?.applications?.hiddenTabs,
  },
  claimsAndDisputes: {
    hiddenTabs: data?.claimsAndDisputes?.hiddenTabs,
  },
});

const defaultPaymentConfig: PaymentConfiguration = {
  paymentProvider: PaymentProvider.Solupay,
  solupay: { publicKey: tenantConfig.paymentToken } as SolupayItem,
  solupayItems: [],
  acceptedCardTypes: tenantConfig?.paymentConfiguration?.acceptedCardTypes,
};

export const defaultStaticContext: SiteConfigView = {
  enabledModules: [],
  quickLinks: [],
  sidebar: [],
  isNccerSsoApp: tenantConfig.theme === SiteTheme.NccerSSO,
  isNccerBillingApp: tenantConfig.theme === SiteTheme.NccerBilling,
  isNccerTheme: tenantConfig.theme === SiteTheme.NccerSSO || tenantConfig.theme === SiteTheme.NccerBilling,
  isMemberplexTheme: tenantConfig.theme === SiteTheme.MemberPlex,
  isDemoTheme: tenantConfig.theme === SiteTheme.Demo,
  isGamsdTheme: tenantConfig.theme === SiteTheme.Gamsd,
  isSnaTheme: tenantConfig.theme === SiteTheme.Sna,
  isAcsTheme: tenantConfig.theme === SiteTheme.Acs,
  isSmacnaTheme: tenantConfig.theme === SiteTheme.Smacna,
  isCompassTheme: tenantConfig.theme === SiteTheme.Compass,
  isTeamsEliteTheme: tenantConfig.theme === SiteTheme.TeamsElite,
  isWcaTheme: tenantConfig.theme === SiteTheme.Wca || tenantConfig.theme === SiteTheme.WcaBlue,
  isUsAseanTheme: tenantConfig.theme === SiteTheme.UsAsean,
  isBayViewTheme: tenantConfig.theme === SiteTheme.BayView,
  isDefaultTheme: tenantConfig.theme === SiteTheme.Default,
  modulesSettings: getDefaultModulesSettingsData(),
  paymentConfiguration: tenantConfig?.paymentConfiguration ?? defaultPaymentConfig,
};

export const ConfigContext = React.createContext<SiteConfigView>(defaultStaticContext);

const ConfigGuard: React.FunctionComponent = ({ children }) => {
  const [staticData, setStaticData] = useState<SiteConfigView>(defaultStaticContext);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const { error, firstLoading, data } = useRequest<SiteConfig>(UserService.getSiteConfig);

  useEffect(() => {
    setErrorMessage(error || '');
  }, [error]);

  useEffect(() => {
    if (data) {
      const {
        enabledModules = [],
        quickLinks = [],
        sidebar = [],
        modulesSettings,
        paymentConfiguration,
        search,
      } = data;

      setStaticData((prevState) => ({
        ...prevState,
        enabledModules,
        quickLinks,
        sidebar,
        paymentConfiguration: paymentConfiguration,
        modulesSettings: getDefaultModulesSettingsData(modulesSettings),
        search,
      }));
    }
  }, [data]);

  return firstLoading ? (
    <Spinner loading={true} fullPage={true} transparent={false} />
  ) : error ? (
    <Alert severity={'error'} className={commonStyles.alert}>
      {errorMessage}
    </Alert>
  ) : (
    <ConfigContext.Provider value={staticData}>{children}</ConfigContext.Provider>
  );
};

export default ConfigGuard;
